import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Collapse,
  IconButton,
  Stack,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Save,
  Cancel,
  Dashboard as DashboardIcon,
  AdminPanelSettings,
  Shield,
  Visibility,
  VisibilityOff,
  Edit,
  Description,
  Folder,
  FolderOpen,
  ExpandMore,
  ExpandLess,
  PlaylistAddCheck,
} from "@mui/icons-material";

import Sidebar from "../components/Sidebar";
import SearchableDropdown from "../components/SearchableDropdown";
import { useAuth } from "../context/AuthContext";
import RbacManager from "../utilities/rbac";

const COLORS = {
  navy: "#00205B",
  lightBlue: "#008CDE",
  deepGreen: "#00594C",
  orange: "#EF7C00",
  steelGrey: "#5E6A71",
  coolGrey: "#D9D9D6",
  lightTint: "#F4F5F6",
  success: "#4CAF50",
  error: "#E65100",
};

const apiHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const getValue = (e) => {
  if (typeof e === "string") return e;
  if (e?.target?.value !== undefined) return e.target.value;
  if (e?.value !== undefined) return e.value;
  return "";
};

const commonPaper = {
  borderRadius: 3,
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 1px rgba(0,0,0,0.02)",
};

const AccessControl = () => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [rolesLoading, setRolesLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [error, setError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");

  const [rbacPages, setRbacPages] = useState([]);
  const [expandedPages, setExpandedPages] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [activePageIndex, setActivePageIndex] = useState(null);

  const rbacMgr = useMemo(
    () =>
      new RbacManager(
        user?.userType === "employee"
          ? user?.roles[0] !== "admin"
            ? user?.designation
            : user?.roles[0]
          : "",
        "accessControl"
      ),
    [user]
  );
  const isE = rbacMgr.isE.bind(rbacMgr);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/access-control/all-roles`, {
        headers: apiHeaders(token),
      });
      const fetchedRoles = Array.isArray(response?.data?.data) ? response.data.data : [];
      setRoles(fetchedRoles);
      setSelectedRole(fetchedRoles.includes("admin") ? "admin" : fetchedRoles[0] || "");
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load roles");
      setRoles([]);
      setSelectedRole("");
    } finally {
      setRolesLoading(false);
    }
  }, [API_BASE_URL, token]);

  const fetchAccessControlByRole = useCallback(
    async (role) => {
      if (!role) {
        setRbacPages([]);
        setExpandedPages({});
        setExpandedGroups({});
        setActivePageIndex(null);
        return;
      }

      setPagesLoading(true);
      setError(null);
      const snakeCaseRole = role.trim().toLowerCase().replace(/[\s-]+/g, "_");

      try {
        const response = await axios.get(`${API_BASE_URL}/access-control/rbac?role=${snakeCaseRole}`, {
          headers: apiHeaders(token),
        });

        const pages = Array.isArray(response?.data?.data) ? response.data.data : [];
        const filteredPages = pages.filter((page) => page.groups && page.groups.length > 0);

        setRbacPages(filteredPages);

        // Initialize all pages as collapsed (false)
        const pageExpanded = {};
        const groupExpanded = {};
        filteredPages.forEach((page) => {
          pageExpanded[page.name] = false;
          page.groups?.forEach((group) => {
            groupExpanded[group.groupId] = false;
          });
        });

        setExpandedPages(pageExpanded);
        setExpandedGroups(groupExpanded);
        setActivePageIndex(null);
        setSavingRows({});
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load access control configuration");
        setRbacPages([]);
        setExpandedPages({});
        setExpandedGroups({});
        setActivePageIndex(null);
        setSavingRows({});
      } finally {
        setPagesLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  useEffect(() => {
    if (token) fetchRoles();
  }, [token, fetchRoles]);

  useEffect(() => {
    if (!selectedRole) {
      setRbacPages([]);
      return;
    }
    fetchAccessControlByRole(selectedRole);
  }, [selectedRole, fetchAccessControlByRole]);

  const roleOptions = useMemo(() => roles.map((role) => ({ name: role, value: role })), [roles]);

  // Toggle page - only the clicked page expands, all others collapse
  const togglePage = useCallback(
    (pageName, pageIndex) => {
      setActivePageIndex(pageIndex);
      
      // Update expanded pages: only the clicked page becomes true, all others false
      setExpandedPages((prev) => {
        const next = {};
        Object.keys(prev).forEach((key) => {
          next[key] = key === pageName;
        });
        return next;
      });
    },
    []
  );

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const handleFieldToggle = useCallback((pageName, groupId, fieldIndex, permissionType) => {
    setRbacPages((prev) =>
      prev.map((page) => {
        if (page.name !== pageName) return page;
        return {
          ...page,
          groups: page.groups.map((group) => {
            if (group.groupId !== groupId) return group;
            const updatedFields = group.fields.map((field, idx) => {
              if (idx !== fieldIndex) return field;
              const updated = { ...field, [permissionType]: !field[permissionType] };
              if (permissionType === "visible" && !updated.visible) updated.editable = false;
              return updated;
            });
            return { ...group, fields: updatedFields };
          }),
        };
      })
    );
  }, []);

  const updatePageFromResponse = useCallback((pageName, groupId, savedFields) => {
    setRbacPages((prev) =>
      prev.map((page) => {
        if (page.name !== pageName) return page;
        return {
          ...page,
          groups: page.groups.map((group) => {
            if (group.groupId !== groupId) return group;

            const nextFields = group.fields.map((field) => {
              const matched = savedFields.find(
                (f) => f.fieldName === field.name || f.fieldName === field.displayName
              );
              if (!matched) return field;
              return {
                ...field,
                visible: !!matched.visible,
                editable: !!matched.editable,
              };
            });

            return { ...group, fields: nextFields };
          }),
        };
      })
    );
  }, []);

  const handleSaveGroup = useCallback(
    async (pageName, groupId) => {
      if (!selectedRole || !pageName || !groupId) return;

      const group = rbacPages.find((p) => p.name === pageName)?.groups?.find((g) => g.groupId === groupId);
      if (!group) return;

      const rowKey = `${pageName}-${groupId}`;
      setSavingRows((prev) => ({ ...prev, [rowKey]: true }));

      try {
        const payload = {
          fields: (group.fields || []).map((field) => ({
            fieldName: field.name,
            visible: !!field.visible,
            editable: !!field.editable,
          })),
        };

        const response = await axios.post(
          `${API_BASE_URL}/access-control/updateMultiplePermissions?role=${selectedRole}&pageName=${pageName}`,
          payload,
          { headers: apiHeaders(token) }
        );

        const responseFields =
          response?.data?.data?.rbac?.[pageName]?.fields ||
          response?.data?.data?.rbac?.[group.groupDisplayName]?.fields ||
          null;

        if (responseFields && typeof responseFields === "object") {
          const savedFields = Object.entries(responseFields).map(([fieldName, perm]) => ({
            fieldName,
            visible: !!perm?.visible,
            editable: !!perm?.editable,
          }));
          updatePageFromResponse(pageName, groupId, savedFields);
        }

        Swal.fire({
          title: t("Success"),
          text: response?.data?.message || t("Permission updated successfully"),
          icon: "success",
          confirmButtonText: t("Ok"),
          confirmButtonColor: COLORS.navy,
          timer: 2000,
          timerProgressBar: true,
        });
      } catch (err) {
        Swal.fire({
          title: t("Error"),
          text: err?.response?.data?.message || err.message || t("Failed to save permission"),
          icon: "error",
          confirmButtonText: t("Ok"),
          confirmButtonColor: COLORS.orange,
        });
      } finally {
        setSavingRows((prev) => ({ ...prev, [rowKey]: false }));
      }
    },
    [API_BASE_URL, rbacPages, selectedRole, t, token, updatePageFromResponse]
  );

  const renderHierarchy = useCallback(() => {
    if (!rbacPages.length) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <PlaylistAddCheck sx={{ fontSize: 56, color: "#cbd5e1", mb: 1 }} />
          <Typography sx={{ color: COLORS.steelGrey, fontWeight: 500 }}>
            {t("No pages with groups available for this role")}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {rbacPages.map((page, originalIndex) => {
          const pageOpen = !!expandedPages[page.name];

          return (
            <Card
              key={page.name}
              sx={{
                ...commonPaper,
                width: "100%",
                border: pageOpen ? `1px solid ${COLORS.lightBlue}` : "1px solid #e5e7eb",
                overflow: "hidden",
                transition: "all 0.2s ease",
              }}
            >
              <Box
                onClick={() => togglePage(page.name, originalIndex)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                  bgcolor: pageOpen ? "#f8fafc" : "#fff",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                  <Avatar sx={{ bgcolor: COLORS.navy, width: 40, height: 40 }}>
                    <DashboardIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>
                      {page.displayName || page.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: COLORS.steelGrey }}>
                      Page permissions
                    </Typography>
                  </Box>
                </Box>

                <IconButton size="small" sx={{ color: COLORS.navy }}>
                  {pageOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={pageOpen} timeout="auto" unmountOnExit>
                <Box sx={{ p: 1.5, bgcolor: COLORS.lightTint }}>
                  <Stack spacing={1.2}>
                    {page.groups.map((group) => {
                      const groupOpen = !!expandedGroups[group.groupId];
                      const groupSaving = !!savingRows[`${page.name}-${group.groupId}`];

                      return (
                        <Paper
                          key={group.groupId}
                          sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid #e5e7eb",
                            bgcolor: "#fff",
                            boxShadow: "0 1px 1px rgba(0,0,0,0.02)",
                          }}
                        >
                          <Box
                            onClick={() => toggleGroup(group.groupId)}
                            sx={{
                              px: 1.5,
                              py: 1.2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              bgcolor: groupOpen ? "#f8fafc" : "#fff",
                              "&:hover": { bgcolor: "#f8fafc" },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  bgcolor: groupOpen ? COLORS.lightBlue : COLORS.deepGreen,
                                }}
                              >
                                {groupOpen ? <FolderOpen fontSize="small" /> : <Folder fontSize="small" />}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1f2937" }}>
                                  {group.groupDisplayName || `Group ${group.groupId}`}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveGroup(page.name, group.groupId);
                                }}
                                disabled={groupSaving || !isE("updatePermissions")}
                                sx={{
                                  color: COLORS.navy,
                                  "&:disabled": { color: "#d1d5db" },
                                }}
                              >
                                {groupSaving ? (
                                  <CircularProgress size={14} sx={{ color: COLORS.navy }} />
                                ) : (
                                  <Save fontSize="small" />
                                )}
                              </IconButton>

                              <IconButton size="small" onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(group.groupId);
                              }}>
                                {groupOpen ? <ExpandLess /> : <ExpandMore />}
                              </IconButton>
                            </Box>
                          </Box>

                          <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 1.4, bgcolor: "#fff" }}>
                              {group.fields?.length ? (
                                <Grid container spacing={1.5}>
                                  {group.fields.map((field, fieldIndex) => {
                                    return (
                                      <Grid key={`${page.name}-${group.groupId}-${fieldIndex}`} item xs={12} sm={6} md={4}>
                                        <Paper
                                          sx={{
                                            border: "1px solid #eef2f7",
                                            borderRadius: 2,
                                            p: 1.1,
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            boxShadow: "0 1px 1px rgba(0,0,0,0.02)",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: 1,
                                              width: "100%",
                                              minWidth: 0,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                width: 160,
                                                flex: 1,
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontWeight: 700,
                                                  color: "#1f2937",
                                                  fontSize: "0.86rem",
                                                  lineHeight: 1.25,
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: 2,
                                                  WebkitBoxOrient: "vertical",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  alignSelf: "center",
                                                }}
                                              >
                                                {field.displayName || field.name}
                                              </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.2, flexShrink: 0 }}>
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleFieldToggle(page.name, group.groupId, fieldIndex, "visible")
                                                }
                                                disabled={!isE("updatePermissions")}
                                                sx={{
                                                  color: field.visible ? COLORS.lightBlue : COLORS.steelGrey,
                                                  p: 0.35,
                                                  "&:hover": { bgcolor: "transparent" },
                                                }}
                                              >
                                                {field.visible ? (
                                                  <Visibility fontSize="small" />
                                                ) : (
                                                  <VisibilityOff fontSize="small" />
                                                )}
                                              </IconButton>

                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleFieldToggle(page.name, group.groupId, fieldIndex, "editable")
                                                }
                                                disabled={!field.visible || !isE("updatePermissions")}
                                                sx={{
                                                  color: field.editable ? COLORS.success : COLORS.steelGrey,
                                                  p: 0.35,
                                                  "&:hover": { bgcolor: "transparent" },
                                                }}
                                              >
                                                <Edit fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        </Paper>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              ) : (
                                <Typography sx={{ textAlign: "center", py: 1.5, color: COLORS.steelGrey }}>
                                  No fields in this group
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              </Collapse>
            </Card>
          );
        })}
      </Stack>
    );
  }, [
    rbacPages,
    expandedPages,
    expandedGroups,
    savingRows,
    handleFieldToggle,
    handleSaveGroup,
    togglePage,
    toggleGroup,
    isE,
    t,
  ]);

  return (
    <Sidebar title={t("Access Control")}>
      <Box sx={{ minHeight: "100vh" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            mb: 3,
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
          }}
        >
          <Card
            sx={{
              ...commonPaper,
              flex: { xs: "1 1 auto", md: "1 1 58%" },
              minWidth: 0,
              width: "100%",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1f2937" }}>
                      {t("Select Role")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.steelGrey }}>
                      {t("Choose a role to load its permissions")}
                    </Typography>
                  </Box>
                  <SearchableDropdown
                    name="role"
                    options={roleOptions}
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(getValue(e))}
                    placeholder={t("Select Role")}
                    isLoading={rolesLoading}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {rolesLoading || pagesLoading ? (
          <Paper sx={{ ...commonPaper, p: 6, textAlign: "center" }}>
            <CircularProgress size={32} sx={{ color: COLORS.lightBlue, mb: 2 }} />
            <Typography color="textSecondary">
              {rolesLoading ? t("Loading roles...") : t("Loading permissions...")}
            </Typography>
          </Paper>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} icon={<Cancel />}>
            {error}
          </Alert>
        ) : null}

        {!rolesLoading && !pagesLoading && !error && selectedRole ? <Box>{renderHierarchy()}</Box> : null}

        {!rolesLoading && !pagesLoading && !error && !selectedRole ? (
          <Paper
            sx={{
              ...commonPaper,
              textAlign: "center",
              py: 8,
              px: 3,
              border: "2px dashed #dbe3ef",
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2,
                bgcolor: COLORS.lightTint,
                color: COLORS.lightBlue,
              }}
            >
              <AdminPanelSettings sx={{ fontSize: 38 }} />
            </Avatar>
            <Typography variant="h6" sx={{ color: "#1f2937", mb: 0.5, fontWeight: 800 }}>
              {t("Please select a role")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("Select a role to view the complete permission hierarchy")}
            </Typography>
          </Paper>
        ) : null}
      </Box>
    </Sidebar>
  );
};

export default AccessControl;